import { TitanPanel } from "@lib/titan2/dist/game/control/TitanPanel";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { ReelEvent, SlingoEvent } from "../../../controller/event/SlingoEvent";
import { SlingoStyle } from "../style/SlingoStyle";
import { AbstractGameView } from "./AbstractGameView";
import { Reel } from "./Reel";
/**
 * Reels.ts
 */
export class Reels extends AbstractGameView {
    get hasBackground() {
        return this.uiProperties?.backgroundProperties?.id?.length > 0;
    }
    get hasBackgroundPurchase() {
        return this.uiProperties.backgroundPurchaseProperties && !this.uiProperties.backgroundProperties.equals(this.uiProperties.backgroundPurchaseProperties);
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(ReelEvent.SHOW, this.show);
        this.registerGame(ReelEvent.SPIN_REEL, this.spinReel);
        this.registerGame(ReelEvent.DISABLE_REELS, this.handleDisableReels);
        this.registerGame(ReelEvent.RECOVER, this.handleRecovery);
        this.registerGame(ReelEvent.RESET, this.resetReels);
        this.registerGame(ReelEvent.STOP_REEL, this.stopReel);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN, this.handlePurchaseEntry);
        this.registerGame(SlingoEvent.GAME_SUMMARY, this.handleGameSummary);
        this.registerGame(SlingoEvent.REELS_AWARD, this.handleReelsAward);
        this.registerGame(SlingoEvent.FREE_SPIN_AWARD, this.handleFreeSpinAward);
    }
    create() {
        super.create();
        this.createBackground();
        this.createReels();
    }
    postCreate() {
        super.postCreate();
        Logger.addObjectRef(this, "Reels");
    }
    destroy() {
        this.landscapeProperties = null;
        this.portraitProperties = null;
        this.uiProperties = null;
        DestroyUtil.destroy(this.background);
        this.background = null;
        DestroyUtil.destroy(this.backgroundPurchase);
        this.backgroundPurchase = null;
        this.freeSpinIndices = null;
        this.destroyReels();
        super.destroy();
    }
    destroyReels() {
        DestroyUtil.destroy(this.panel);
        this.panel = null;
        this.reels = null;
        Reel.resetIndex();
    }
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    createBackground() {
        if (this.hasBackground) {
            this.background = TitanSprite.createSprite(this, "background", this.landscapeProperties.backgroundProperties, this.portraitProperties?.backgroundProperties);
        }
        if (this.hasBackgroundPurchase) {
            this.backgroundPurchase = TitanSprite.createSprite(this, "backgroundPurchase", this.landscapeProperties.backgroundPurchaseProperties, this.portraitProperties?.backgroundPurchaseProperties);
            this.backgroundPurchase.renderable = false;
        }
    }
    createReels() {
        this.panel = TitanPanel.createPanel(this, "reelsPanel", this.landscapeProperties.panelProperties, this.portraitProperties?.panelProperties);
        this.reels = [];
        let reel;
        for (let i = 0; i < this.model.getReelDisplayData().getNumberOfReels(); i++) {
            reel = this.factory.createReel();
            this.panel.addToPanel(reel, false);
            this.reels.push(reel);
            reel.setTrackingId(`${SlingoStyle.REEL}_${i}`);
        }
        this.panel.arrange();
    }
    handleDisableReels() {
        let indexes = this.model.getReelDisplayData().getDisabledIndexes();
        for (let index of indexes) {
            let reel = this.reels[index];
            if (reel) {
                reel.disable();
            }
        }
    }
    handleRecovery() {
        const symbolPool = this.model.getReelDisplayData().getDisplaySymbols().slice();
        while (symbolPool.length > this.reels.length) {
            symbolPool.shift();
        }
        let reel;
        for (let i = 0; i < this.reels.length; i++) {
            const id = symbolPool[i].getId();
            reel = this.reels[i];
            reel.stop(id);
        }
    }
    handlePurchaseEntry() {
        if (this.hasBackgroundPurchase) {
            this.background.renderable = false;
            this.backgroundPurchase.renderable = true;
        }
        this.reels.forEach(reel => reel.updateBackground(true));
    }
    handleGameSummary() {
        this.background && (this.background.renderable = true);
        this.backgroundPurchase && (this.backgroundPurchase.renderable = false);
        this.reels.forEach(reel => reel.updateBackground(false));
    }
    handleReelsAward() {
        this.freeSpinIndices = this.model.getSpinResult().getReelSequence().getFreeSpinIndexes();
    }
    handleFreeSpinAward() {
        const next = this.freeSpinIndices.shift();
        const symbolIds = this.model.getReelDisplayData().getDisplaySymbols().map(symbol => symbol.getId());
        if (typeof next === "number" && this.reels[next]) {
            this.reels[next].stop(symbolIds[next]);
        }
    }
    disableReels() {
        for (let i = 0; i < this.reels.length; i++) {
            this.reels[i].disable();
        }
    }
    spinReel() {
        let index = this.model.getReelDisplayData().getStartIndex();
        this.reels[index].play();
        this.model.getReelDisplayData().spin();
    }
    stopReel() {
        const stopIndex = this.model.getReelDisplayData().getStopIndex();
        const symbol = this.model.getReelDisplayData().getStopSymbol();
        const reel = this.reels[stopIndex];
        if (reel) {
            Logger.debug(`<------------ STOP REEL ${stopIndex}-------------->`); //`
            reel.stop(symbol.getId());
        }
        this.checkSuspense();
    }
    checkSuspense() {
        const stopIndex = this.model.getReelDisplayData().getStopIndex();
        const scatterCount = this.model.getScatterCount();
        const scatterThreshold = this.model.getScatterThreshold();
        if (scatterThreshold > -1 && scatterCount >= scatterThreshold) {
            this.reels.slice(stopIndex).forEach((reel) => reel.suspense());
        }
    }
    resetReels() {
        for (let reel of this.reels) {
            reel.reset();
        }
    }
}
//# sourceMappingURL=Reels.js.map